import create from 'zustand';
import {
  SyncFlowEventSubject$
} from '../../sync-flow-v2/components/CalSyncDialog';
import {
  authorizeIcsCalendarImport,
  connectCalendar,
  disconnectCalendar,
  fetchCronofyCalendars,
  fetchCronofyLinkForSpace
} from '../../../../../api/calendarSync';
import { get as getLodash } from 'lodash';
import useCalendarStore from '../calendar/useCalendarStore';
import { SyncStatus } from '../../connect-calendar/util/syncStatus';

export const CAL_TYPE_SYNC = 'sync';
export const CAL_TYPE_ICAL = 'i_cal';

const useCalendarSyncFlowStore = create((set, get) => ({
  spaceId: null,
  calendarType: null,
  cronofyCalendars: [],
  selectedCronofyCalId: null,
  isConnectingCal: false,
  iCalName: null,
  iCalUrl: null,
  iCalUrlError: null,
  iCalNameError: null,
  setICalName: (name) => set({ iCalName: name }),
  setICalUrl: (url) => set({ iCalUrl: url }),
  setSyncStatus: (status) => set({ syncStatus: status }),
  setSelectedCronofyCalId: (id) => set({ selectedCronofyCalId: id }),
  setSpaceId: (id) => set({ spaceId: id }),
  setCalendarType: (type) => set({ calendarType: type }),
  fetchCronofyLink: async () => {
    const { spaceId } = get();
    const { data, error } = await fetchCronofyLinkForSpace(spaceId);
    if (error) {
      SyncFlowEventSubject$.next({ type: 'error', errorMessage: error.message });
      return;
    }

    const { cronofy_link: cronofyLink } = data;
    SyncFlowEventSubject$.next({
      type: 'redirect',
      link: cronofyLink
    });
  },
  initCalendarSelection: async (selectedSpaceId) => {
    const { data, error } = await fetchCronofyCalendars(selectedSpaceId);
    const { calendars = [] } = data ?? {};

    if (error) {
      SyncFlowEventSubject$.next({ type: 'error', errorMessage: error.message });

      return;
    }

    if (0 === calendars.length) {
      const { error } = await disconnectCalendar(selectedSpaceId);
      if (!error) {
        useCalendarStore.getState().setSyncStatus(SyncStatus.NOT_CONNECTED);
      }

      SyncFlowEventSubject$.next({ type: 'no_cals_to_sync' });

      return;
    }

    if (1 === calendars.length) {
      const { syncCronofyCalendar } = get();
      await syncCronofyCalendar(selectedSpaceId, getLodash(calendars, '[0].calendar_id', null));
    }

    const cronofyCalendars = calendars.map((cal) => ({
      id: cal.calendar_id,
      name: cal.calendar_name
    }));

    set({ cronofyCalendars });
  },
  syncCronofyCalendar: async (selectedSpace, calIdOverride) => {
    set({ isConnectingCal: true });
    const { selectedCronofyCalId } = get();
    const { error } = await connectCalendar(selectedSpace, calIdOverride ?? selectedCronofyCalId);
    if (error) {
      SyncFlowEventSubject$.next({ type: 'error', errorMessage: error.message });
      set({ isConnectingCal: false });
      return;
    }

    SyncFlowEventSubject$.next({
      type: 'success',
      calType: 'external_cal',
      syncedSpaceId: selectedSpace
    });
    set({ isConnectingCal: false });
  },
  handleICalValidation: () => {
    const {
      iCalName,
      iCalUrl,
      iCalUrlError,
      iCalNameError
    } = get();

    if (iCalName && iCalNameError) {
      set({ iCalNameError: null });
    }

    if (iCalUrl && iCalUrlError) {
      set({ iCalUrlError: null });
    }

    let hasError = false;
    if (!iCalName) {
      set({
        iCalNameError: 'Please enter a calendar name',
        isConnectingCal: false
      });
      hasError = true;
    }

    if (!iCalUrl) {
      set({
        iCalUrlError: 'Please enter a URL',
        isConnectingCal: false
      });
      hasError = true;
    }

    return !hasError;
  },
  syncICalendar: async () => {
    set({ isConnectingCal: true });

    const { spaceId, iCalUrl, iCalName } = get();

    const authorizeRes = await authorizeIcsCalendarImport(spaceId, iCalUrl, iCalName ?? 'iCalendar');
    if (authorizeRes.error) {
      if (400 === authorizeRes.status) {
        set({ iCalUrlError: 'Please enter a valid URL' });
      } else {
        SyncFlowEventSubject$.next({ type: 'error', errorMessage: authorizeRes.error.message });
      }
      set({ isConnectingCal: false });
      return;
    }

    const fetchCalsRes = await fetchCronofyCalendars(spaceId);

    const { calendars } = fetchCalsRes?.data ?? {};
    const calendarId = getLodash(calendars, '[0].calendar_id', null);

    if (fetchCalsRes.error || !calendarId) {
      SyncFlowEventSubject$.next({ type: 'error', errorMessage: fetchCalsRes.error.message });
      set({ isConnectingCal: false });
      return;
    }

    const connectRes = await connectCalendar(spaceId, calendarId);
    if (connectRes.error) {
      SyncFlowEventSubject$.next({ type: 'error', errorMessage: connectRes.error.message });
      set({ isConnectingCal: false });
      return;
    }

    SyncFlowEventSubject$.next({
      type: 'success',
      calType: 'i_cal',
      syncedSpaceId: spaceId
    });
    set({ isConnectingCal: false });
  }
}));

export default useCalendarSyncFlowStore;
